// Demo mode ("sandbox") — window.VFP_DEMO.
//
// The REAL app, run against a seeded, ephemeral tenant with every production
// side effect disabled. Entering /demo starts a session; from then on the
// store reads/writes a separate localStorage key, the cloud sync module
// short-circuits (nothing reaches Supabase), and window.VFP_API_BASE is
// removed so every integration / AI / billing / SMS call falls into the code
// paths the app already has for "no service configured" — zero paid calls.
// The few things the sandbox DOES talk to the server for (geocoding, the one
// allowlisted email, the mocked deposit, the hub link on the visitor's phone)
// go through VFP_DEMO.api() → /demo/<sid>/… with a session header.
//
// This module must run BEFORE lib/supabase and before any screen module
// (see main.tsx import order) — it decides the mode at boot from the URL.

import { buildDemoSeed } from "./demoseed";
import { rekeyState } from "./rekey";

const KEY = "fw_demo_v1";              // { sid, startedAt, lastActive, expiresAt, prefill, prospect, utm }
const STATE_KEY = "vfp_demo_state_v1"; // the sandbox's own store mirror (never vfp_state_v2)
const PROMOTE_KEY = "fw_promote_demo"; // state carried into a real workspace on "Keep this"
const TTL_MS = 30 * 60 * 1000;         // sliding 30-minute session
const TOUCH_EVERY_MS = 20 * 1000;

const SEED_COMPANY = "Demo Fence Co";

function readJson(k: string) { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch (e) { return null; } }
function writeJson(k: string, v: any) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
function del(k: string) { try { localStorage.removeItem(k); } catch (e) {} }
function hex(bytes: number) {
  const a = new Uint8Array(bytes);
  try { crypto.getRandomValues(a); } catch (e) { for (let i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256); }
  return Array.from(a).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const entering = /^\/demo\/?$/.test(window.location.pathname || "");
const now0 = Date.now();
let expiredOnBoot = false;
let sess: any = readJson(KEY);
if (sess && (!sess.sid || Number(sess.expiresAt) < now0)) { expiredOnBoot = !!sess.sid; sess = null; del(KEY); del(STATE_KEY); }

let params: URLSearchParams | null = null;
let fresh = false;
if (entering) {
  params = new URLSearchParams(window.location.search || "");
  if (!sess || params.get("reset")) {
    sess = { sid: hex(16), startedAt: now0, lastActive: now0, expiresAt: now0 + TTL_MS };
    del(STATE_KEY);
    fresh = true;
  }
  // one-field hero box on the marketing site → lands on the estimator with the
  // address prefilled (phase 5); signed prospect token pre-loads a company
  // (phase 4). Kept on the session, never in the app URL.
  const address = (params.get("address") || "").trim().slice(0, 200);
  if (address) sess.prefill = { address };
  const prospect = (params.get("t") || "").trim().slice(0, 600);
  if (prospect) sess.prospect = prospect;
  const utm: any = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "ref"].forEach((k) => { const v = params!.get(k); if (v) utm[k] = String(v).slice(0, 120); });
  if (Object.keys(utm).length) sess.utm = utm;
  if (document.referrer) sess.referrer = String(document.referrer).slice(0, 300);
  sess.lastActive = now0; sess.expiresAt = now0 + TTL_MS;
  writeJson(KEY, sess);
  // clean URL: the sandbox is the ordinary app from here on (dashboard) — or
  // the screen a deep link asked for (`go=quote&quoteId=…`, used by the
  // marketing site's side-panel previews), keeping `embed=1` so the app
  // renders without its chrome inside that frame
  let landing = "/";
  const go = String(params.get("go") || "").trim();
  if (/^[a-z][a-z0-9-]{1,30}$/.test(go)) {
    const keep = new URLSearchParams();
    const SESSION_KEYS = new Set(["go", "reset", "address", "t", "ref", "utm_source", "utm_medium", "utm_campaign", "utm_content"]);
    params.forEach((v, k) => { if (!SESSION_KEYS.has(k) && /^[a-zA-Z][a-zA-Z0-9_]{0,30}$/.test(k)) keep.set(k, String(v).slice(0, 120)); });
    const qs = keep.toString();
    landing = "/" + (go === "dashboard" ? "" : go) + (qs ? "?" + qs : "");
    // public routes: the customer's portal (local sandbox state) and the 3D viewer
    if (go === "hub") { const tok = String(params.get("token") || "demo-seed-hub-1"); landing = /^[a-z0-9-]{4,64}$/i.test(tok) ? "/hub/" + tok + "?demo_frame=1" : "/"; }
    if (go === "build3d") landing = "/build3d?embed=1";
  }
  try { window.history.replaceState({}, "", landing); } catch (e) {}
}

const active = !!sess;
// the automation-service base is swapped out of the global so every existing
// "no service configured" branch takes over (integrations connect locally,
// AI/SMS/billing/render paths refuse without a network call), while the demo
// router keeps it for its own allowlisted calls.
const apiBase = active ? String((window as any).VFP_API_BASE || "").replace(/\/$/, "") || null : null;
if (active) { try { delete (window as any).VFP_API_BASE; } catch (e) { (window as any).VFP_API_BASE = undefined; } }

let lastTouch = 0;
function touch() {
  if (!active || !sess) return;
  const t = Date.now();
  if (t - lastTouch < TOUCH_EVERY_MS) return;
  lastTouch = t;
  sess.lastActive = t; sess.expiresAt = t + TTL_MS;
  writeJson(KEY, sess);
}

function clearAll() { del(KEY); del(STATE_KEY); }

function track(event: string, props?: Record<string, any>) {
  const payload = Object.assign({ event, demo_session: sess ? sess.sid : null, demo_prospect: sess && sess.prospect ? "yes" : "no" }, sess && sess.utm ? sess.utm : {}, props || {});
  try { const w: any = window; (w.dataLayer = w.dataLayer || []).push(payload); } catch (e) {}
  // server copy (phase 3 persists it; for now it lives on the session)
  if (active && apiBase) api("/event", { event, props: props || {} }).catch(() => {});
}

// server calls scoped to this session — the ONLY server traffic a sandbox
// makes besides the keyless geocoder proxy.
async function api(path: string, body?: any, method?: string) {
  if (!active || !apiBase || !sess) throw new Error("Sandbox server isn't available");
  const r = await fetch(`${apiBase}/demo/${encodeURIComponent(sess.sid)}${path}`, {
    method: method || (body ? "POST" : "GET"),
    headers: Object.assign({ "Content-Type": "application/json", "X-Demo-Session": sess.sid }),
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e: any = new Error(d.message || d.error || ("sandbox " + r.status)); e.status = r.status; e.code = d.error; throw e; }
  return d;
}

// register the session with the server (rate limits live there); resolves the
// server's view: enabled? busy? Local-only when there's no service at all.
const server: any = { enabled: apiBase ? null : false, busy: false, checked: !apiBase };
async function register() {
  if (!active || !apiBase) return server;
  try {
    const d = await api("/session", { startedAt: sess.startedAt, prospect: sess.prospect || null, utm: sess.utm || null, referrer: sess.referrer || null });
    server.enabled = d.enabled !== false; server.busy = false;
    if (d.email) sess.email = d.email;   // resumed session already captured an address
  } catch (e: any) {
    if (e && (e.status === 404 || e.code === "disabled")) server.enabled = false;
    else if (e && e.status === 429) { server.enabled = true; server.busy = true; }
    else server.enabled = true;   // transient — stay usable, email will retry
  }
  server.checked = true;
  try { window.dispatchEvent(new CustomEvent("fw:demo-server", { detail: server })); } catch (e) {}
  return server;
}

// expiry watchdog — drops the sandbox once the sliding window lapses
let expired = false;
if (active) {
  setInterval(() => {
    const cur = readJson(KEY);
    if (!cur || Number(cur.expiresAt) < Date.now()) {
      if (expired) return;
      expired = true;
      track("demo_expired");
      clearAll();
      try { window.dispatchEvent(new Event("fw:demo-expired")); } catch (e) {}
    }
  }, 30 * 1000);
}

/* what a visitor takes with them into a real Free workspace: the records THEY
   made (or worked), their pricing/crews/tax, their branding — never the
   seeded lead/customers/jobs untouched, never the sandbox plan. */
function carryState(company?: string) {
  const s = readJson(STATE_KEY);
  if (!s) return null;
  const keptQuotes = (s.quotes || []).filter((q: any) => q && (!q.demoSeed || (q.demoSeedStatus && q.status !== q.demoSeedStatus)));
  const qids = new Set(keptQuotes.map((q: any) => q.id));
  const custIds = new Set(keptQuotes.map((q: any) => q.customerId).filter(Boolean));
  const keptCustomers = (s.customers || []).filter((c: any) => c && (!c.demoSeed || custIds.has(c.id)));
  const keptProjects = (s.projects || []).filter((p: any) => p && (!p.demoSeed || qids.has(p.fromQuote)));
  const pids = new Set(keptProjects.map((p: any) => p.id));
  const keptWos = (s.workOrders || []).filter((w: any) => w && (!w.demoSeed || qids.has(w.quoteId) || pids.has(w.projectId)));
  const settings = Object.assign({}, s.settings || {});
  ["plan", "subStatus", "usageCache", "ai", "widget", "budget", "cash", "salesReps", "leadRRIdx", "demo"].forEach((k) => { delete settings[k]; });
  const co = Object.assign({}, s.company || {});
  const typedName = String(co.name || settings.companyName || "").trim();
  const name = (company || "").trim() || (typedName && typedName !== SEED_COMPANY ? typedName : "");
  if (name) { co.name = name; settings.companyName = name; } else { delete co.name; delete settings.companyName; }
  // the sandbox company's contact details are not theirs
  ["phone", "email", "address", "street", "city", "state", "zip"].forEach((k) => { delete co[k]; });
  ["companyPhone", "companyCity", "companyState"].forEach((k) => { delete settings[k]; });
  const strip = (x: any) => { const y = Object.assign({}, x); delete y.demoSeed; delete y.demoSeedStatus; return y; };
  // seeded ids ("cust-demo-1", "q-demo-1", hub token "demo-seed-hub-1") are
  // the same in every sandbox; customers/quotes/projects/work orders have
  // global primary keys in the cloud, so a second promoted workspace would
  // collide with the first and never sync (dead hub links). Fresh ids for the
  // seeded ones; records the visitor made already carry collision-proof ids.
  const { state: keyed } = rekeyState({
    settings, quotes: keptQuotes.map(strip), customers: keptCustomers.map(strip),
    projects: keptProjects.map(strip), workOrders: keptWos.map(strip),
  });
  return {
    settings: keyed.settings, company: co, companyReady: true,
    customProducts: (s.customProducts || []).map(strip),
    quotes: keyed.quotes, customers: keyed.customers,
    projects: keyed.projects, workOrders: keyed.workOrders,
    activity: [],
  };
}

/* "Keep this as my free workspace": create the account, stash the carried
   state, and hand off to the real app (App applies it after it hydrates). */
async function promote({ email, password, name, company }: any) {
  const DB: any = (window as any).VFP_DB;
  if (!(DB && DB.auth && DB.auth.signUp)) throw new Error("Sign-up isn't available right now");
  const carry = carryState(company);
  if (carry) writeJson(PROMOTE_KEY, carry);
  const r = await DB.auth.signUp(email, password, name, company);
  track("workspace_promoted", { has_session: !!(r && r.session) });
  if (apiBase && sess) api("/promoted", { email }).catch(() => {});
  try { localStorage.setItem("fw_pending_plan", "free"); } catch (e) {}
  clearAll();
  return { session: !!(r && r.session) };
}

const seedCache: any = { state: null };
function seed() {
  if (!seedCache.state) seedCache.state = buildDemoSeed({ startedAt: sess ? sess.startedAt : Date.now(), prefill: sess && sess.prefill });
  return JSON.parse(JSON.stringify(seedCache.state));
}

function setTour(t: any) { if (sess) { sess.tour = t; writeJson(KEY, sess); } }

const VFP_DEMO = {
  active, expiredOnBoot,
  get tour() { return sess ? sess.tour || null : null; },
  setTour,
  clearPrefill() { if (sess) { delete sess.prefill; writeJson(KEY, sess); } (VFP_DEMO as any).prefill = null; },
  estimator: null as any,        // the Estimator registers { search(addr), lf() } while mounted (tour step 2)
  founderEndsLabel: "Nov 30, 2026",
  sessionId: sess ? sess.sid : null,
  startedAt: sess ? sess.startedAt : null,
  prefill: (sess && sess.prefill) || null,
  prospect: (sess && sess.prospect) || null,
  get email() { return sess ? sess.email || null : null; },
  setEmail(e: string) { if (sess) { sess.email = e; writeJson(KEY, sess); } },
  get company() { return sess ? sess.company || null : null; },
  setCompany(c: string) { if (sess) { sess.company = c; writeJson(KEY, sess); } },
  apiBase, server, register, api, track, touch,
  stateKey: STATE_KEY, promoteKey: PROMOTE_KEY, seedCompany: SEED_COMPANY, ttlMs: TTL_MS,
  seed, carryState, promote,
  store: null as any,           // the live store api (App publishes it) — used by the tour + tests
  // "Start over": drop everything and re-enter with a fresh seed
  reset() { track("demo_reset"); clearAll(); window.location.href = "/demo?reset=1"; },
  // leave the sandbox (back to the real app / sign-in)
  exit() { clearAll(); window.location.href = "/"; },
  isExpired() { return expired; },
  // customer-facing docs carry a SAMPLE watermark until the visitor brands them
  branded(state: any) {
    const co = (state && state.company) || {};
    const nm = String(co.name || (state && state.settings && state.settings.companyName) || "").trim();
    return !!(co.logo || (nm && nm !== SEED_COMPANY));
  },
};
(window as any).VFP_DEMO = VFP_DEMO;

if (active) {
  if (fresh) track("demo_started", { has_address: sess && sess.prefill ? "yes" : "no", has_prospect: sess && sess.prospect ? "yes" : "no" });
  // every load, fresh or resumed — the homepage's demo_open_click pairs with this
  track("demo_loaded", { fresh: fresh ? "yes" : "no" });
  register();
}

export { VFP_DEMO, STATE_KEY as DEMO_STATE_KEY, PROMOTE_KEY as DEMO_PROMOTE_KEY };
